import { observer } from "mobx-react"
import { useMemo } from "react"

import { MultiLanguageTextField } from "src/components/Inputs/TextFields/MultiLanguageTextField"
import { getSectionFieldName } from "src/components/ConfigurableDynamicFormFields/helpers/fieldNameHelpers"
import { IMultiLanguageTextFieldValue } from "src/types/modal-text-field"
import { ModalTextField } from "src/components/ModalTextField"
import { ConfigurableDynamicFormFieldsStore } from "src/components/ConfigurableDynamicFormFields/store"
import { useStore } from "src/store/lib/useStore"
import { IConfigurableDynamicFormFieldsTextFieldProps } from "src/components/ConfigurableDynamicFormFields/types/form-field"

export const ConfigurableDynamicFormFieldsTextField = observer(
    ({
        sectionName,
        field,
        index,
        variant = "withTooltip",
    }: IConfigurableDynamicFormFieldsTextFieldProps) => {
        const store = useStore(ConfigurableDynamicFormFieldsStore)
        // fields value can also be an array of data, incase there's field.length attribute
        const _formFieldKey = useMemo(
            () => getSectionFieldName(sectionName, field.name, index),
            [field.name, index, sectionName],
        )
        const textFieldAttributes = useMemo(() => {
            const isTextArea = field.type === "textarea"
            const fallbackMaxLength = isTextArea ? 100000 : 150
            const maxLength = field.config?.max_length ?? fallbackMaxLength

            return {
                type: field.type === "integer" ? "number" : undefined,
                rows: isTextArea ? 4 : 1,
                maxLength,
            }
        }, [field.config?.max_length, field.type])

        const tooltipText = useMemo(
            () => field.tooltip_key ?? "",
            [field.tooltip_key],
        )

        return (
            <>
                {field.localised === true ? (
                    <MultiLanguageTextField
                        variant={variant}
                        label={field.label}
                        fieldName={_formFieldKey}
                        data={
                            store.form.get(_formFieldKey) as
                                | IMultiLanguageTextFieldValue
                                | undefined // in case fo create new
                        }
                        onChange={store.form.setter(_formFieldKey)}
                        onSecondaryLanguageChange={store.form.setter(
                            _formFieldKey,
                        )}
                        helperText={store.form.error(_formFieldKey)}
                        error={Boolean(store.form.error(_formFieldKey))}
                        disabled={store.isReadOnly}
                        tooltipText={tooltipText}
                        defaultValue={field.default_value}
                        rows={textFieldAttributes.rows}
                        maxLength={textFieldAttributes.maxLength}
                    />
                ) : (
                    <ModalTextField
                        variant="withToolTip"
                        label={field.label}
                        value={store.form.get(_formFieldKey) as string | number}
                        onChange={store.form.setter(_formFieldKey)}
                        helperText={store.form.error(_formFieldKey)}
                        error={Boolean(store.form.error(_formFieldKey))}
                        disabled={store.isReadOnly}
                        tooltipText={tooltipText}
                        defaultValue={field.default_value}
                        rows={textFieldAttributes.rows}
                        maxLength={textFieldAttributes.maxLength}
                        type={textFieldAttributes.type}
                    />
                )}
            </>
        )
    },
)
